/**
 * Instellingen → Rechten (admin, actieve winkel). Batjoens-wensen 10, 11, 12, 13:
 *  - wie ziet alle dossiers vs. enkel de eigen (per rol)
 *  - mag iedereen milestones aanpassen
 *  - welke rollen mogen (naast admin) verwijderen
 *  - statussymbolen (★ ? ! …) op dossiers: naam, icoon uit vaste set, kleur
 * De server controleert alles opnieuw; deze pagina bewaart enkel de instellingen van DEZE winkel.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Check } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { useToast } from '../shared/components/Toast.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { useAuth } from '../auth/AuthContext.js';
import { MARKERING_ICONEN, markeringSymbool, type MarkeringIcoon } from '../rechten/markeringIconen.js';
import type { DossierMarkeringType } from '../../modules/buitendienst/types/index.js';

interface WinkelRechten {
  entityId: string;
  milestonesIedereen: boolean;
  eigenDossiersRollen: string[];
  instelbaar: { zichtbaarheid: string[] };
}

const ROL_LABELS: Record<string, string> = {
  manager: 'Winkeldirecteur (manager)',
  binnendienst: 'Verkoper / binnendienst',
  buitendienst: 'Buitendienst / plaatser',
  chauffeur: 'Chauffeur',
};

const KLEUR_VOORSTELLEN = ['#D97706', '#2563EB', '#DC2626', '#059669', '#7C3AED', '#DB2777', '#0891B2', '#4B5563'];

export function RechtenTab() {
  const { activeEntity } = useAuth();
  const winkel = activeEntity?.name ?? 'deze winkel';
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Deze instellingen gelden enkel voor <strong>{winkel}</strong>. Wissel van winkel om een andere winkel in te stellen.</p>
      <KleurVoorstellen />
      <div className="grid gap-6 lg:grid-cols-2">
        <RechtenKaart />
        <MarkeringenKaart />
      </div>
    </div>
  );
}

function RechtenKaart() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['rechten', 'winkel'], queryFn: () => apiFetch<WinkelRechten>('/api/rechten/winkel') });
  const [milestonesIedereen, setMilestonesIedereen] = useState(true);
  const [eigen, setEigen] = useState<string[]>([]);

  useEffect(() => {
    if (!data) return;
    setMilestonesIedereen(data.milestonesIedereen);
    setEigen(data.eigenDossiersRollen);
  }, [data]);

  const bewaar = useMutation({
    mutationFn: () => apiFetch<WinkelRechten>('/api/rechten/winkel', {
      method: 'PUT',
      body: JSON.stringify({ milestonesIedereen, eigenDossiersRollen: eigen }),
    }),
    onSuccess: () => {
      toast('success', 'Rechten opgeslagen');
      qc.invalidateQueries({ queryKey: ['rechten'] });
      qc.invalidateQueries({ queryKey: ['buitendienst'] });
    },
    onError: () => toast('error', 'Fout bij opslaan rechten'),
  });

  if (isLoading || !data) return <Card><p className="text-sm text-gray-400">Laden…</p></Card>;
  const wissel = (lijst: string[], zet: (v: string[]) => void, rol: string) => zet(lijst.includes(rol) ? lijst.filter(r => r !== rol) : [...lijst, rol]);

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Rechten per rol</h2>
      <p className="text-xs text-gray-500 mb-4">Admin en eigenaar zien en mogen altijd alles. Plaatsers zien enkel dossiers waaraan ze toegewezen zijn.</p>

      <h3 className="text-sm font-semibold text-gray-800 mb-2">Welke dossiers ziet een rol?</h3>
      <table className="w-full text-sm mb-5">
        <tbody>
          {data.instelbaar.zichtbaarheid.map(rol => {
            const enkelEigen = eigen.includes(rol);
            return (
              <tr key={rol} className="border-b border-gray-100 last:border-0">
                <td className="py-2 text-gray-700">{ROL_LABELS[rol] ?? rol}</td>
                <td className="py-2 text-right">
                  <div className="inline-flex bg-gray-100 p-0.5 rounded-lg">
                    {[false, true].map(waarde => (
                      <button
                        key={String(waarde)}
                        type="button"
                        onClick={() => setEigen(waarde ? [...new Set([...eigen, rol])] : eigen.filter(r => r !== rol))}
                        className={clsx('px-3 py-1 rounded-md text-xs font-medium', enkelEigen === waarde ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')}
                      >
                        {waarde ? 'Enkel eigen' : 'Alle dossiers'}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-gray-400 -mt-3 mb-5">"Eigen" = verkoper (naam of initialen), verantwoordelijke, aanmaker, toegewezen lead of ingepland op een stap/afspraak.</p>

      <label className="flex items-start gap-3 mb-5 cursor-pointer">
        <input type="checkbox" checked={milestonesIedereen} onChange={e => setMilestonesIedereen(e.target.checked)} className="mt-0.5 w-4 h-4 accent-gold" />
        <span>
          <span className="block text-sm font-semibold text-gray-800">Iedereen mag de milestones van elk dossier aanpassen</span>
          <span className="block text-xs text-gray-500">Uit: enkel admin, eigenaar, winkeldirecteur en de eigen mensen van het dossier mogen trajectstappen verzetten.</span>
        </span>
      </label>


      <Button onClick={() => bewaar.mutate()} disabled={bewaar.isPending}>
        <Check size={14} /> Rechten opslaan
      </Button>
    </Card>
  );
}

function MarkeringenKaart() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['rechten', 'markeringen', 'beheer'],
    queryFn: () => apiFetch<{ types: DossierMarkeringType[] }>('/api/rechten/markeringen/beheer'),
  });
  const [nieuw, setNieuw] = useState<{ naam: string; icoon: MarkeringIcoon; kleur: string }>({ naam: '', icoon: 'vlag', kleur: KLEUR_VOORSTELLEN[3] });

  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['rechten', 'markeringen'] });
    qc.invalidateQueries({ queryKey: ['buitendienst'] });
  };
  const wijzig = useMutation({
    mutationFn: ({ id, ...body }: Partial<DossierMarkeringType> & { id: string }) =>
      apiFetch(`/api/rechten/markeringen/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: verversen,
    onError: () => toast('error', 'Statussymbool bewaren mislukt'),
  });
  const maak = useMutation({
    mutationFn: () => apiFetch('/api/rechten/markeringen', { method: 'POST', body: JSON.stringify(nieuw) }),
    onSuccess: () => { verversen(); setNieuw(n => ({ ...n, naam: '' })); toast('success', 'Statussymbool toegevoegd'); },
    onError: () => toast('error', 'Statussymbool toevoegen mislukt'),
  });

  if (isLoading || !data) return <Card><p className="text-sm text-gray-400">Laden…</p></Card>;

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Statussymbolen op dossiers</h2>
      <p className="text-xs text-gray-500 mb-4">Medewerkers zetten deze symbolen op een dossier (zichtbaar en filterbaar in de dossierlijst). Uitzetten verbergt het symbool overal, zonder iets te wissen.</p>
      <div className="space-y-2 mb-4">
        {data.types.map(t => (
          <MarkeringRij key={t.id} type={t} onBewaar={body => wijzig.mutate({ id: t.id, ...body })} />
        ))}
      </div>
      <div className="border-t border-gray-100 pt-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Nieuw symbool</h3>
        <div className="flex flex-wrap items-center gap-2">
          <IcoonKeuze waarde={nieuw.icoon} kleur={nieuw.kleur} onChange={icoon => setNieuw(n => ({ ...n, icoon }))} />
          <KleurKeuze waarde={nieuw.kleur} onChange={kleur => setNieuw(n => ({ ...n, kleur }))} />
          <input
            value={nieuw.naam}
            maxLength={60}
            onChange={e => setNieuw(n => ({ ...n, naam: e.target.value }))}
            placeholder="Betekenis, bv. 'Klant terugbellen'"
            className="flex-1 min-w-[160px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
          />
          <Button variant="secondary" size="sm" disabled={!nieuw.naam.trim() || maak.isPending} onClick={() => maak.mutate()}>
            <Plus size={14} /> Toevoegen
          </Button>
        </div>
      </div>
    </Card>
  );
}

function MarkeringRij({ type, onBewaar }: { type: DossierMarkeringType; onBewaar: (body: Partial<DossierMarkeringType>) => void }) {
  const [naam, setNaam] = useState(type.naam);
  useEffect(() => setNaam(type.naam), [type.naam]);
  return (
    <div className={clsx('flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5', type.actief === false ? 'bg-gray-50 opacity-60' : 'bg-gray-50')}>
      <IcoonKeuze waarde={type.icoon as MarkeringIcoon} kleur={type.kleur} onChange={icoon => onBewaar({ icoon })} />
      <KleurKeuze waarde={type.kleur} onChange={kleur => onBewaar({ kleur })} />
      <input
        value={naam}
        maxLength={60}
        onChange={e => setNaam(e.target.value)}
        onBlur={() => { if (naam.trim() && naam.trim() !== type.naam) onBewaar({ naam: naam.trim() }); }}
        className="flex-1 min-w-[140px] px-2 py-1 text-sm bg-transparent border border-transparent hover:border-gray-200 focus:border-gold rounded outline-none"
      />
      <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
        <input type="checkbox" checked={type.actief !== false} onChange={e => onBewaar({ actief: e.target.checked })} className="w-4 h-4 accent-gold" />
        Actief
      </label>
    </div>
  );
}

function IcoonKeuze({ waarde, kleur, onChange }: { waarde: MarkeringIcoon; kleur: string; onChange: (v: MarkeringIcoon) => void }) {
  return (
    <span className="relative inline-flex items-center">
      <span className="w-7 h-7 rounded-md flex items-center justify-center text-white text-sm font-bold pointer-events-none absolute left-0" style={{ backgroundColor: kleur }}>
        {markeringSymbool(waarde)}
      </span>
      <select
        aria-label="Icoon"
        value={waarde}
        onChange={e => onChange(e.target.value as MarkeringIcoon)}
        className="w-7 h-7 opacity-0 cursor-pointer"
      >
        {(Object.keys(MARKERING_ICONEN) as MarkeringIcoon[]).map(k => (
          <option key={k} value={k}>{MARKERING_ICONEN[k]} {k}</option>
        ))}
      </select>
    </span>
  );
}

/** Kleurkiezer: lokaal bijhouden tijdens het slepen, pas bij loslaten/verlaten doorgeven (geen PATCH-regen). */
function KleurKeuze({ waarde, onChange }: { waarde: string; onChange: (v: string) => void }) {
  const [lokaal, setLokaal] = useState(waarde);
  useEffect(() => setLokaal(waarde), [waarde]);
  const commit = () => { if (lokaal.toUpperCase() !== waarde.toUpperCase()) onChange(lokaal.toUpperCase()); };
  return (
    <input
      type="color"
      aria-label="Kleur"
      value={lokaal}
      list="markering-kleuren"
      onChange={e => setLokaal(e.target.value)}
      onBlur={commit}
      onMouseUp={commit}
      className="w-7 h-7 rounded cursor-pointer border border-gray-200 p-0"
    />
  );
}

/** Kleurvoorstellen voor de kleurkiezer (één keer in de DOM). */
function KleurVoorstellen() {
  return (
    <datalist id="markering-kleuren">
      {KLEUR_VOORSTELLEN.map(k => <option key={k} value={k} />)}
    </datalist>
  );
}
